import * as m from '../../common/messages.js';
import {outgoing} from '../../common/messages.js';
import {activityDomainUri} from './constants.js';
import {Factory, FactoryId} from '../../state/types/factory.js';
import {PeerId, Source} from '../../state/types/common.js';
import {Reason} from '../../reason.js';
import {SendMessageAction} from '../../node.js';
import {Activity, ActivityChild, ActivityChildren, ActivityId, ActivityType} from '../../state/types/activity.js';
import {Peer, State} from '../../state/types/state.js';
import {byId} from '../../state/peers.js';
import {Draft, Immutable, produce} from 'immer';
import {
    ActivityJoined,
    Created,
    DisposePeer,
    Initiated,
    Joined,
    Left,
    OwnerChanged,
    PeerCreated,
    PeerFactoriesAdded,
    PeerFactoriesRemoved,
    PeerInfo,
    PeerRequested,
    TypesAdded,
    TypesRemoved
} from './types/messages.js';
import {ContextData} from '../../state/types/context.js';


function peerFactoriesAddedMsg(peerId: PeerId, ownerId: PeerId, factories: Factory[]): PeerFactoriesAdded {
    return {
        domain: activityDomainUri,
        type: 'peer-factories-added',
        peer_id: peerId,
        owner_id: ownerId,
        factories: factories
    };
}

export function peerFactoriesAdded(recipient: Source, peerId: PeerId, ownerId: PeerId, factories: Factory[]): SendMessageAction<'peer-factories-added'> {
    return outgoing(recipient, peerFactoriesAddedMsg(peerId, ownerId, factories));
}

function peerFactoriesRemovedMsg(peerId: PeerId, ownerId: PeerId, factoryIds: FactoryId[]): PeerFactoriesRemoved {
    return {
        domain: activityDomainUri,
        type: 'peer-factories-removed',
        peer_id: peerId,
        owner_id: ownerId,
        factory_ids: factoryIds
    };
}

export function peerFactoriesRemoved(recipient: Source, peerId: PeerId, ownerId: PeerId, factoryIds): SendMessageAction<'peer-factories-removed'> {
    return outgoing(recipient, peerFactoriesRemovedMsg(peerId, ownerId, factoryIds));
}

function peerRequestedMsg(requestId: string, peerId: PeerId, factoryId: FactoryId, token: string, configuration: object, activityRq): PeerRequested {
    return {
        domain: activityDomainUri,
        type: 'peer-requested',
        request_id: requestId,
        peer_id: peerId,
        peer_factory: factoryId,
        gateway_token: token,
        configuration,
        ...activityRq
    };
}

export function peerRequested(recipient: Source, requestId, peerId: PeerId, factoryId: FactoryId, token: string, configuration: object, activityRq): SendMessageAction<'peer-requested'> {
    return outgoing(recipient, peerRequestedMsg(requestId, peerId, factoryId, token, configuration, activityRq));
}

export function disposePeer(recipient: Source, peerId: PeerId, requestorId, reason: Reason): SendMessageAction<'dispose-peer'> {
    const message: DisposePeer = {
        domain: activityDomainUri,
        type: 'dispose-peer',
        peer_id: peerId,
        requestor_id: requestorId,
        reason_uri: reason.uri,
        reason: reason.message
    };
    return outgoing(recipient, message);
}

function peerCreatedMsg(requestId, peerId: PeerId, createdId: PeerId): PeerCreated {
    return {
        domain: activityDomainUri,
        type: 'peer-created',
        request_id: requestId,
        peer_id: peerId,
        created_id: createdId
    }
}

export function peerCreated(recipient: Source, requestId, peerId: PeerId, createdId: PeerId): SendMessageAction<'peer-created'> {
    return outgoing(recipient, peerCreatedMsg(requestId, peerId, createdId));
}

export function activityTypesAdded(recipient: Source, peerId: PeerId, types: ActivityType[]): SendMessageAction<'types-added'> {
    const message: TypesAdded = {domain: activityDomainUri, type: 'types-added', peer_id: peerId, types: types};
    return outgoing(recipient, message);
}

export function activityTypesRemoved(recipient: Source, peerId: PeerId, types: string[]): SendMessageAction<'types-removed'> {
    const message: TypesRemoved = {domain: activityDomainUri, type: 'types-removed', peer_id: peerId, types: types};
    return outgoing(recipient, message);
}

export function activityInitiated(recipient: Source, requestId: string, peerId: PeerId, activityId: ActivityId): SendMessageAction<'initiated'> {
    const message: Initiated = {
        domain: activityDomainUri,
        type: 'initiated',
        request_id: requestId,
        peer_id: peerId,
        activity_id: activityId
    };
    return outgoing(recipient, message);
}


type ActivityWithFilteredContext = Immutable<{
    context?: ContextData
    children?: { [id: string]: ActivityWithFilteredContext }
}>

function filterContext(children: ActivityChildren): ActivityWithFilteredContext {
    return Object.entries(children).reduce((acc, [k, c]) => {
        acc[k] = produce(c as ActivityWithFilteredContext, (draft: Draft<ActivityWithFilteredContext>) => {
            draft.context = (draft as ActivityChild).context.data;
            if (draft.children) {
                draft.children = filterContext(draft.children as ActivityChildren);
            }
        });
        return acc;
    }, {} as ActivityWithFilteredContext);
}

function peerInfo(state: State, peerId?: PeerId): PeerInfo {
    const p = byId(state, peerId);
    return {peer_id: peerId, name: p?.peerName, type: p?.peerType};
}

function joinedActivityMsg(state: State, peer: Peer, activity: Activity, contextSnapshot: ContextData): Joined {
    const children = activity.children;
    const peerName = peer.peerName;
    const peerType = peer.peerType;
    const msg: Joined = {
        domain: activityDomainUri,
        type: 'joined',
        peer_id: peer.id,
        activity_id: activity.id,
        activity_type: activity.type,
        initiator: activity.initiator,
        context_id: activity.contextId,
        owner: peerInfo(state, activity.owner),
        participants: Array.from<PeerId>(activity.readyMembers || []).map((peerId) => peerInfo(state, peerId)),
        context_snapshot: contextSnapshot
    };
    if (peerName) {
        msg['peer_name'] = peerName;
    }
    if (peerType) {
        msg['peer_type'] = peerType;
    }
    if (children) {
        msg['children'] = filterContext(children)
    }

    return msg;
}

export function joinedActivity(state: State, recipient: Source, peer: Peer, activity, contextSnapshot: ContextData): SendMessageAction<'joined'> {
    return outgoing(recipient, joinedActivityMsg(state, peer, activity, contextSnapshot));
}

function activityJoinedMsg(peerId: PeerId, joinedPeer: Peer, activityId): ActivityJoined {
    const peerName = joinedPeer.peerName;
    const msg: ActivityJoined = {
        domain: activityDomainUri,
        type: 'activity-joined',
        peer_id: peerId,
        activity_id: activityId,
        joined_id: joinedPeer.id,
        joined_type: joinedPeer.peerType
    };
    if (peerName) msg['peer_name'] = peerName;
    return msg;
}

export function activityJoined(recipient: Source, peerId: PeerId, joinedPeer, activityId): SendMessageAction<'activity-joined'> {
    return outgoing(recipient, activityJoinedMsg(peerId, joinedPeer, activityId));
}

function activityCreatedMsg(state: State, peerId: PeerId, activity: Activity): Created {
    const children = activity.children;
    const msg: Created = {
        domain: activityDomainUri,
        type: 'created',
        peer_id: peerId,
        activity_id: activity.id,
        context_id: activity.contextId,
        owner: peerInfo(state, activity.owner),
        participants: Array.from<PeerId>(activity.readyMembers || []).concat(Array.from<PeerId>(activity.participants || [])).map((peerId) => peerInfo(state, peerId)),
        activity_type: activity.type,
        initiator: activity.initiator
    };
    if (children) {
        msg['children'] = filterContext(children);
    }
    return msg;
}

export function activityCreated(state: State, recipient: Source, peerId: PeerId, activity: Activity): SendMessageAction<'created'> {
    return outgoing(recipient, activityCreatedMsg(state, peerId, activity));
}

export function activityLeft(recipient: Source, peerId: PeerId, leftId: PeerId, activityId, reason: Reason): SendMessageAction<'left'> {
    const message: Left = {
        domain: activityDomainUri,
        type: 'left',
        peer_id: peerId,
        activity_id: activityId,
        left_id: leftId,
        reason_uri: reason.uri,
        reason: reason.message
    };
    return outgoing(recipient, message);
}

function ownerChangedMsg(peerId: PeerId, activityId: string, owner: Peer): OwnerChanged {
    const peerName = owner.peerName;
    const peerType = owner.peerType;
    const msg: OwnerChanged = {
        domain: activityDomainUri,
        type: 'owner-changed',
        peer_id: peerId,
        activity_id: activityId,
        owner_id: owner.id
    };
    if (peerName) msg['peer_name'] = peerName;
    if (peerType) msg['peer_type'] = peerType;
    return msg;
}

export function ownerChanged(recipient: Source, peerId: PeerId, activityId: string, owner: Peer): SendMessageAction<'owner-changed'> {
    return outgoing(recipient, ownerChangedMsg(peerId, activityId, owner));
}

export function success(recipient: Source, requestId, peerId): SendMessageAction<'success'> {
    return m.success(activityDomainUri, recipient, requestId, peerId);
}
export function error(recipient: Source, requestId, peerId, reason, context?): SendMessageAction<'error'> {
    return m.error(activityDomainUri, recipient, requestId, peerId, reason, context);
}
export function peerRemoved(recipient: Source, peerId, removedPeerId, reason: Reason): SendMessageAction<'peer-removed'> {
    return m.peerRemoved(activityDomainUri, recipient, peerId, removedPeerId, reason);
}

export function peerAdded(recipient: Source, peerId, newPeerId, newPeerIdentity, meta): SendMessageAction<'peer-added'> {
    return m.peerAdded(activityDomainUri, recipient, peerId, newPeerId, newPeerIdentity, meta);
}
